import { useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { formatAgo, formatDateTime } from '../lib/format'
import type { Customer, PortFinderResult } from '../lib/types'
import { ErrorBox, Loading } from '../components/Feedback'
import { GeraeteImNetz } from '../components/Endgeraete'

interface Props {
  customerSlug: string | null
}

/**
 * Port-Finder: wo hängt ein Gerät?
 *
 * Die Suche folgt dem Weg, den ein Techniker von Hand ginge: ARP-Tabelle des
 * Gateways, dann die MAC-Tabellen der Switches. Der Port mit den wenigsten
 * Adressen ist der Access-Port (docs/design/screens/PortFinder.dc.html).
 */
export function PortFinderPage({ customerSlug }: Props) {
  const [input, setInput] = useState('')
  const [query, setQuery] = useState('')

  const result = useQuery({
    queryKey: ['portfinder', query],
    queryFn: ({ signal }) =>
      apiGet<PortFinderResult>(`/portfinder?q=${encodeURIComponent(query)}`, signal),
    enabled: query !== '',
  })

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    enabled: customerSlug !== null,
  })
  const customer = customerSlug ? customers.data?.find((c) => c.slug === customerSlug) : undefined

  function submit(e: FormEvent) {
    e.preventDefault()
    setQuery(input.trim())
  }

  function suchen(mac: string) {
    setInput(mac)
    setQuery(mac)
    document.getElementById('portfinder-suche')?.focus()
  }

  const data = result.data

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Port-Finder</h1>
          <div className="page-sub">
            {customerSlug ?? 'Alle Kunden'} · Suche über ARP- und MAC-Tabellen
          </div>
        </div>
      </div>

      <section className="card">
        <div className="card-body">
          <form className="row" style={{ gap: 8 }} onSubmit={submit}>
            <label className="sr-only" htmlFor="portfinder-suche">
              IP, MAC oder Gerätename
            </label>
            <input
              id="portfinder-suche"
              className="input grow"
              placeholder="IP-Adresse, MAC-Adresse oder Gerätename"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button type="submit" className="btn btn-primary" disabled={input.trim() === ''}>
              Suchen
            </button>
          </form>
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Die Tabellen werden alle zehn Minuten gelesen. Ein Gerät, das gerade aus ist, steht
            deshalb noch eine Weile in der Liste.
          </p>
        </div>
      </section>

      {query === '' ? null : result.isPending ? (
        <Loading what="Suchergebnis" />
      ) : result.isError ? (
        <ErrorBox error={result.error} what="Die Suche" />
      ) : !data?.gefunden ? (
        <section className="card">
          <div className="empty">
            <strong>Nichts gefunden</strong>
            <span>{data?.meldung ?? 'Zu dieser Eingabe gibt es keinen Eintrag.'}</span>
          </div>
        </section>
      ) : (
        <>
          <section className="card">
            <div className="card-head">
              <h2 className="card-title">
                Gefunden: {data.access_device_name} · {data.access_port_name}
              </h2>
              <span className="muted" style={{ fontSize: 12 }}>
                {data.ip ? `${data.ip} · ` : ''}
                <span className="mono">{data.mac}</span>
              </span>
            </div>
            <div className="card-body stack" style={{ gap: 14 }}>
              <ol className="hops">
                {data.weg.map((hop, i) => (
                  <li key={`${hop.name}-${i}`} className={hop.access ? 'hop hop-access' : 'hop'}>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {hop.rolle}
                    </span>
                    <strong>
                      {hop.device_id ? (
                        <Link
                          to="/k/$customerSlug/geraete/$deviceId"
                          params={{ customerSlug: customerSlug ?? '', deviceId: hop.device_id }}
                        >
                          {hop.name}
                        </Link>
                      ) : (
                        <span className="mono">{hop.name}</span>
                      )}
                    </strong>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {hop.detail}
                    </span>
                  </li>
                ))}
              </ol>

              {data.link_wechsel >= 6 ? (
                <p className="hinweis" style={{ background: 'var(--warn-bg)', margin: 0 }}>
                  <strong style={{ color: 'var(--warn)' }}>Dieser Port flappt</strong>
                  <span>
                    {data.link_wechsel} Link-Wechsel in 10 Minuten. Kabel, Dose oder Netzwerkkarte
                    prüfen.
                  </span>
                </p>
              ) : null}

              <dl className="dialog-facts">
                <dt>VLAN</dt>
                <dd>{data.vlan || 'unbekannt'}</dd>
                <dt>Port</dt>
                <dd className="mono">{data.access_port_name}</dd>
                <dt>Adressen am Port</dt>
                <dd>
                  {data.macs_am_port}
                  {data.macs_am_port === 1 ? ' (Access-Port)' : ''}
                </dd>
                <dt>Zuletzt gesehen</dt>
                <dd>{data.zuletzt_gesehen ? formatAgo(data.zuletzt_gesehen) : '–'}</dd>
                <dt>Link-Wechsel</dt>
                <dd>{data.link_wechsel} in 10 Minuten</dd>
              </dl>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h2 className="card-title">Verlauf dieser MAC-Adresse</h2>
            </div>
            {data.verlauf.length === 0 ? (
              <div className="empty">
                <span>Noch kein Verlauf.</span>
              </div>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th>Zuletzt gesehen</th>
                    <th>Gerät · Port</th>
                    <th>VLAN</th>
                    <th>Seit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.verlauf.map((s, i) => (
                    <tr key={`${s.device_id}-${s.port}-${i}`}>
                      <td>{formatAgo(s.last_seen)}</td>
                      <td>
                        {s.device_name} · <span className="mono">{s.port_name || s.port}</span>
                      </td>
                      <td>{s.vlan || '–'}</td>
                      <td>{formatDateTime(s.first_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {customerSlug && !customer ? null : <GeraeteImNetz customer={customer} onSuchen={suchen} />}
    </>
  )
}
